import { Routes, Route, Navigate } from 'react-router-dom';
import { Frame } from '@shopify/polaris';
import { NavMenu } from '@shopify/app-bridge-react';
import { Navigation } from './components/Navigation';
import { isWoo } from './lib/auth';
import { useTranslation, useLanguage } from './i18n/LanguageContext';
import { Dashboard } from './pages/Dashboard';
import { GoogleAds } from './pages/GoogleAds';
import { Meta } from './pages/Meta';
import { GA4 } from './pages/GA4';
import { TikTok } from './pages/TikTok';
import { Pinterest } from './pages/Pinterest';
import { EventStream } from './pages/EventStream';
import { Billing } from './pages/Billing';
import { Settings } from './pages/Settings';
import { Webhooks } from './pages/Webhooks';
import { Snapchat } from './pages/Snapchat';
import { Analytics } from './pages/Analytics';

// Shared route table for both platforms.
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/google-ads" element={<GoogleAds />} />
      <Route path="/meta" element={<Meta />} />
      <Route path="/ga4" element={<GA4 />} />
      <Route path="/tiktok" element={<TikTok />} />
      <Route path="/pinterest" element={<Pinterest />} />
      <Route path="/events" element={<EventStream />} />
      <Route path="/snapchat" element={<Snapchat />} />
      <Route path="/webhooks" element={<Webhooks />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/settings" element={<Settings />} />
      {/* Legacy step-wizard retired (audit P2-4): the SetupCard on the
          Dashboard is the single onboarding surface. Classic-OAuth installs
          that still get redirected here land on the Dashboard. */}
      <Route path="/onboarding" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Shopify native navigation. App Bridge's <NavMenu> is a web component that
 * injects these links into the Shopify admin's own left sidebar (the "main
 * menu"), instead of rendering a second sidebar inside our iframe. The first
 * link MUST carry rel="home" — Shopify uses it as the app's home target.
 * <NavMenu> captures its <a> text nodes at first render and does not react to
 * i18n changes, so key={language} forces a remount when the merchant switches
 * language.
 */
function ShopifyNavMenu() {
  const t = useTranslation();
  const { language } = useLanguage();
  return (
    <NavMenu key={language}>
      <a href="/" rel="home">{t.nav.dashboard}</a>
      <a href="/google-ads">{t.nav.googleAds}</a>
      <a href="/meta">{t.nav.meta}</a>
      <a href="/ga4">GA4</a>
      <a href="/tiktok">TikTok</a>
      <a href="/pinterest">Pinterest</a>
      <a href="/snapchat">Snapchat</a>
      <a href="/events">Live Events</a>
      <a href="/webhooks">Webhooks</a>
      <a href="/analytics">Analytics</a>
      <a href="/billing">{t.nav.billing}</a>
      <a href="/settings">{t.nav.settings}</a>
    </NavMenu>
  );
}

export default function App() {
  // WooCommerce (hosted dashboard, no Shopify admin shell): keep the custom
  // Polaris sidebar inside the frame. Shopify (embedded): use the native admin
  // sidebar via App Bridge <NavMenu> and render the frame WITHOUT a navigation
  // prop so we don't paint a duplicate in-iframe sidebar.
  if (isWoo()) {
    return (
      <Frame navigation={<Navigation />}>
        <AppRoutes />
      </Frame>
    );
  }

  return (
    <Frame>
      <ShopifyNavMenu />
      <AppRoutes />
    </Frame>
  );
}
